import { ChevronRight, ExternalLink, Globe, Award, FileText, MapPin, AlertCircle, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import Seo from '../components/Seo';
import { OrganizationSchema } from '../components/SchemaMarkup';

function Resources() {
  const breadcrumbs = [
    { label: 'Resources', path: '/resources' },
  ];

  const industryLinks = [
    {
      category: 'Aviation Authorities',
      icon: <Globe size={24} />,
      links: [
        {
          name: 'IATA - International Air Transport Association',
          url: 'https://www.iata.org/',
          description: 'Global standards for airline safety, operations, and passenger services',
        },
        {
          name: 'ICAO - International Civil Aviation Organization',
          url: 'https://www.icao.int/',
          description: 'United Nations agency setting standards for international aviation',
        },
        {
          name: 'FAA - Federal Aviation Administration (USA)',
          url: 'https://www.faa.gov/',
          description: 'US aviation safety and regulations authority',
        },
        {
          name: 'EASA - European Union Aviation Safety Agency',
          url: 'https://www.easa.europa.eu/',
          description: 'European aviation safety standards and certification',
        },
      ],
    },
    {
      category: 'Airport Resources',
      icon: <MapPin size={24} />,
      links: [
        {
          name: 'IATA Airport Codes Directory',
          url: 'https://www.iata.org/en/publications/directories/code-search/',
          description: 'Complete directory of international airport codes (IATA & ICAO)',
        },
        {
          name: 'ACI - Airports Council International',
          url: 'https://www.aci.aero/',
          description: 'Global organization representing airport operators',
        },
        {
          name: 'World Airport Directory',
          url: 'https://www.world-airport-codes.com/',
          description: 'Comprehensive international airport information and codes',
        },
        {
          name: 'Mombasa International Airport (MBA)',
          url: 'https://www.mombasa-airport.com/',
          description: 'Official website of our home airport in Kenya',
        },
        {
          name: 'Nairobi Jomo Kenyatta International Airport (NBO)',
          url: 'https://www.nbo.co.ke/',
          description: 'East Africa\'s largest airport and international hub',
        },
      ],
    },
    {
      category: 'Travel & Hospitality Standards',
      icon: <Award size={24} />,
      links: [
        {
          name: 'UNWTO - World Tourism Organization',
          url: 'https://www.unwto.org/',
          description: 'UN specialized agency for sustainable tourism and travel standards',
        },
        {
          name: 'WTTC - World Travel & Tourism Council',
          url: 'https://www.wttc.org/',
          description: 'Industry platform representing global travel and tourism',
        },
        {
          name: 'Global Travel Association',
          url: 'https://www.gtatravel.org/',
          description: 'Association for travel professionals and services',
        },
        {
          name: 'ISO Standards for Service Quality',
          url: 'https://www.iso.org/category/80256.html',
          description: 'International standards for service quality and customer satisfaction',
        },
      ],
    },
    {
      category: 'Government Travel & Safety',
      icon: <AlertCircle size={24} />,
      links: [
        {
          name: 'US State Department Travel Advisories',
          url: 'https://travel.state.gov/travel/advisories/',
          description: 'Official US travel alerts and destination information',
        },
        {
          name: 'UK Foreign Office Travel Advisories',
          url: 'https://www.gov.uk/foreign-travel-advice',
          description: 'UK government travel advice for all destinations',
        },
        {
          name: 'Kenya Tourism Board',
          url: 'https://www.magicalkenya.com/',
          description: 'Official Kenya tourism and destination information',
        },
        {
          name: 'TSA - Transportation Security Administration',
          url: 'https://www.tsa.gov/travel',
          description: 'US airport security requirements and travel information',
        },
      ],
    },
    {
      category: 'Passenger Rights & Regulations',
      icon: <FileText size={24} />,
      links: [
        {
          name: 'IATA Passenger Bill of Rights',
          url: 'https://www.iata.org/en/programs/intelligence/industry-insights/passenger-bill-of-rights/',
          description: 'International standards for passenger rights and protections',
        },
        {
          name: 'EU261 - Passenger Rights Regulation',
          url: 'https://ec.europa.eu/transport/themes/passengers/en',
          description: 'European Union passenger compensation and rights legislation',
        },
        {
          name: 'DOT - US Department of Transportation',
          url: 'https://www.transportation.gov/airconsumer',
          description: 'US air consumer protection and passenger rights',
        },
      ],
    },
    {
      category: 'Industry Publications & Data',
      icon: <BarChart3 size={24} />,
      links: [
        {
          name: 'Airline Business Magazine',
          url: 'https://www.flightglobal.com/airlinebusiness/',
          description: 'Leading publication for airline industry analysis and trends',
        },
        {
          name: 'Aviation Daily',
          url: 'https://www.aviationdaily.com/',
          description: 'Daily aviation news and industry intelligence',
        },
        {
          name: 'Skytrax - Airport & Airline Reviews',
          url: 'https://www.airlinequality.com/',
          description: 'Independent passenger reviews and airport/airline ratings',
        },
      ],
    },
  ];

  return (
    <div className="font-sans text-black">
      <Seo
        title="Industry Resources & Links | Moreflex Aviation"
        description="Authoritative aviation and travel resources: IATA, ICAO, airport codes, passenger rights, government travel advisories, and industry standards curated by Moreflex Aviation."
        path="/resources"
      />
      <OrganizationSchema />
      <Header />
      <Breadcrumb items={breadcrumbs} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-black via-[#0b1f33] to-[#1a3c55] text-white py-20 overflow-hidden">
        <div className="hero-pattern absolute inset-0" />
        <div className="container relative mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="eyebrow justify-center mb-4 animate-fadeInTop">Knowledge Hub</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fadeInTop">
              Industry Resources & Links
            </h1>
            <p className="text-lg text-white/75 mb-8 animate-fadeIn">
              Curated collection of authoritative resources, industry standards, and government information 
              to support travelers and aviation professionals.
            </p>
          </div>
        </div>

        <div className="absolute top-0 left-0 w-32 h-32 bg-[#73BDF2] rounded-full opacity-10 transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-[#73BDF2] rounded-full opacity-10 transform translate-x-1/4 translate-y-1/4"></div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gray-50 p-8 rounded-2xl border-l-4 border-[#73BDF2]">
              <h2 className="text-2xl font-bold mb-4">Why These Resources Matter</h2>
              <p className="text-gray-700 mb-4">
                Moreflex Aviation is committed to operating under the highest international standards 
                and regulations. The following resources represent the authoritative bodies, government 
                agencies, and industry organizations that guide global aviation services.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <ChevronRight className="text-[#73BDF2] mr-3 mt-1 flex-shrink-0" size={18} />
                  <span>Stay informed about aviation safety and security standards</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="text-[#73BDF2] mr-3 mt-1 flex-shrink-0" size={18} />
                  <span>Understand passenger rights and protections</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="text-[#73BDF2] mr-3 mt-1 flex-shrink-0" size={18} />
                  <span>Access travel advisories and destination information</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="text-[#73BDF2] mr-3 mt-1 flex-shrink-0" size={18} />
                  <span>Follow industry best practices and regulations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Resources by Category */}
      <section className="py-20 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {industryLinks.map((category, idx) => (
              <div key={idx}>
                <div className="flex items-center mb-8">
                  <div className="bg-[#73BDF2]/10 p-4 rounded-full mr-4">
                    <div className="text-[#73BDF2]">{category.icon}</div>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold">{category.category}</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {category.links.map((link, linkIdx) => (
                    <a
                      key={linkIdx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="card-surface p-6 group hover:border-[#73BDF2]"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-bold text-gray-800 group-hover:text-[#73BDF2] transition-colors duration-300 flex-1">
                          {link.name}
                        </h3>
                        <ExternalLink className="text-[#73BDF2] ml-3 flex-shrink-0" size={18} />
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {link.description}
                      </p>
                      <div className="mt-4 text-xs text-[#73BDF2] font-semibold group-hover:translate-x-1 transition-transform duration-300">
                        Visit Resource →
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <p className="eyebrow justify-center mb-3">Compliance</p>
            <h2 className="section-title mb-8 text-center">Our Commitment to Standards</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-[#73BDF2]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="text-[#73BDF2]" size={32} />
                </div>
                <h3 className="font-bold mb-2">IATA Standards</h3>
                <p className="text-gray-600 text-sm">
                  Comply with International Air Transport Association standards for safety and service
                </p>
              </div>
              <div className="text-center">
                <div className="bg-[#73BDF2]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="text-[#73BDF2]" size={32} />
                </div>
                <h3 className="font-bold mb-2">Global Regulations</h3>
                <p className="text-gray-600 text-sm">
                  Adhere to international aviation regulations and security requirements
                </p>
              </div>
              <div className="text-center">
                <div className="bg-[#73BDF2]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="text-[#73BDF2]" size={32} />
                </div>
                <h3 className="font-bold mb-2">Passenger Rights</h3>
                <p className="text-gray-600 text-sm">
                  Protect passenger rights and ensure transparent service delivery
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Resources */}
      <section className="py-20 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <p className="eyebrow justify-center mb-3">Learn More</p>
            <h2 className="section-title mb-8 text-center">Explore Our Services</h2>
            <p className="text-gray-600 text-center mb-8">
              Learn how we apply these industry standards to deliver exceptional service.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link
                to="/services"
                className="card-surface p-6 group hover:border-[#73BDF2]"
              >
                <h3 className="font-bold text-lg mb-2 group-hover:text-[#73BDF2] transition-colors">
                  Our Services
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Premium airport services designed to exceed international standards
                </p>
                <div className="text-[#73BDF2] font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Explore Services →
                </div>
              </Link>

              <Link
                to="/partners"
                className="card-surface p-6 group hover:border-[#73BDF2]"
              >
                <h3 className="font-bold text-lg mb-2 group-hover:text-[#73BDF2] transition-colors">
                  Partners & Affiliations
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Industry partnerships that ensure quality and reliability
                </p>
                <div className="text-[#73BDF2] font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More →
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-black via-[#0b1f33] to-[#1a3c55] text-white overflow-hidden">
        <div className="hero-pattern absolute inset-0" />
        <div className="container relative mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Book Premium Airport Services?</h2>
          <p className="text-lg text-white/75 mb-8 max-w-2xl mx-auto">
            Contact us today to experience exceptional service backed by international standards and expertise.
          </p>
          <Link
            to="/contact"
            className="btn-primary"
          >
            Contact Us <ChevronRight size={20} className="ml-2" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Resources;
